import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AlertCircle,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Edit2,
  Loader2,
  Plus,
  RefreshCw,
  Save,
  Trash2,
  X,
} from 'lucide-react';

import { useSchool } from '../../../context/SchoolContext';
import { academicApi } from '../../academics/services/academicApi';
import {
  feesApi,
  getFeesApiErrorMessage,
} from '../services/feesApi';

const FREQUENCIES = ['ONE_TIME', 'MONTHLY', 'TERM', 'ANNUAL', 'CUSTOM'];
const STUDENT_SCOPES = [
  { value: 'ALL_STUDENTS', label: 'All students' },
  { value: 'NEW_ADMISSIONS_ONLY', label: 'New admissions only' },
];
const LATE_FEE_TYPES = [
  { value: 'NONE', label: 'No late fee' },
  { value: 'FIXED_PER_DAY', label: 'Fixed per day' },
  { value: 'FIXED_PER_WEEK', label: 'Fixed per week' },
  { value: 'FLAT_AFTER_GRACE', label: 'Flat after grace' },
];

const emptyInstallment = (index = 0) => ({
  installment_no: index + 1,
  name: `Installment ${index + 1}`,
  amount: '',
  due_date: '',
  grace_period_days: 0,
  late_fee_type: 'NONE',
  late_fee_amount: '0.00',
  status: 'ACTIVE',
});

const emptyComponent = () => ({
  category: '',
  code: '',
  name: '',
  frequency: 'ANNUAL',
  amount: '',
  due_date: '',
  late_fee_per_day: '0.00',
  is_mandatory: true,
  student_scope: 'ALL_STUDENTS',
  status: 'ACTIVE',
  installments: [],
});

const emptyGroup = () => ({
  name: '',
  code: '',
  description: '',
  status: 'ACTIVE',
  class_level_ids: [],
  components: [emptyComponent()],
});

const money = (value) =>
  `₹${Number(value || 0).toLocaleString('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const formatDate = (value) => {
  if (!value) return '—';
  const date = new Date(`${value}T00:00:00`);
  return Number.isNaN(date.getTime())
    ? value
    : date.toLocaleDateString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      });
};

const humanize = (value) =>
  String(value || '')
    .toLowerCase()
    .replaceAll('_', ' ')
    .replace(/\b\w/g, (letter) => letter.toUpperCase());

const Modal = ({ title, subtitle, onClose, children, maxWidth = 'max-w-5xl' }) => (
  <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/50 p-4">
    <div className={`max-h-[94vh] w-full ${maxWidth} overflow-hidden rounded-2xl bg-white shadow-2xl`}>
      <div className="flex items-start justify-between border-b border-gray-200 px-6 py-4">
        <div>
          <h2 className="text-lg font-bold text-gray-900">{title}</h2>
          {subtitle && <p className="mt-1 text-xs text-gray-500">{subtitle}</p>}
        </div>
        <button
          type="button"
          onClick={onClose}
          className="rounded-lg p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
        >
          <X className="h-5 w-5" />
        </button>
      </div>
      <div className="max-h-[82vh] overflow-y-auto p-6">{children}</div>
    </div>
  </div>
);

const Notice = ({ type = 'error', children }) => {
  if (!children) return null;
  const isError = type === 'error';
  return (
    <div
      className={`flex items-start gap-2 rounded-xl border p-3 text-sm ${
        isError
          ? 'border-red-200 bg-red-50 text-red-700'
          : 'border-green-200 bg-green-50 text-green-700'
      }`}
    >
      {isError ? (
        <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
      ) : (
        <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" />
      )}
      <span>{children}</span>
    </div>
  );
};

const Field = ({ label, required = false, children, hint }) => (
  <label className="block">
    <span className="text-sm font-medium text-gray-700">
      {label}
      {required && <span className="ml-1 text-red-500">*</span>}
    </span>
    {children}
    {hint && <span className="mt-1 block text-xs text-gray-500">{hint}</span>}
  </label>
);

const inputClass =
  'mt-1 w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 disabled:bg-gray-100 disabled:text-gray-500';

const sanitizeComponent = (component) => {
  const installments = (component.installments || []).map((item, index) => ({
    installment_no: Number(item.installment_no || index + 1),
    name: String(item.name || `Installment ${index + 1}`).trim(),
    amount: Number(item.amount || 0).toFixed(2),
    due_date: item.due_date,
    grace_period_days: Number(item.grace_period_days || 0),
    late_fee_type: item.late_fee_type || 'NONE',
    late_fee_amount:
      item.late_fee_type === 'NONE'
        ? '0.00'
        : Number(item.late_fee_amount || 0).toFixed(2),
    status: item.status || 'ACTIVE',
  }));

  return {
    category: component.category,
    code: String(component.code || '').trim().toUpperCase().replaceAll(' ', '_'),
    name: String(component.name || '').trim(),
    frequency: component.frequency || 'ONE_TIME',
    amount: Number(component.amount || 0).toFixed(2),
    due_date: installments.length ? null : component.due_date || null,
    late_fee_per_day: Number(component.late_fee_per_day || 0).toFixed(2),
    is_mandatory: Boolean(component.is_mandatory),
    student_scope: component.student_scope || 'ALL_STUDENTS',
    status: component.status || 'ACTIVE',
    installments,
  };
};

const validateComponent = (component, index) => {
  const label = component.name?.trim() || `Component ${index + 1}`;
  if (!component.category) return `${label}: fee category is required.`;
  if (!component.code?.trim()) return `${label}: component code is required.`;
  if (!component.name?.trim()) return `Component ${index + 1}: name is required.`;
  if (!component.amount || Number(component.amount) <= 0) {
    return `${label}: amount must be greater than zero.`;
  }

  const installments = component.installments || [];
  if (installments.length > 0) {
    let total = 0;
    for (let itemIndex = 0; itemIndex < installments.length; itemIndex += 1) {
      const item = installments[itemIndex];
      if (!item.name?.trim()) return `${label}: installment ${itemIndex + 1} needs a name.`;
      if (!item.amount || Number(item.amount) <= 0) {
        return `${label}: installment ${itemIndex + 1} amount must be greater than zero.`;
      }
      if (!item.due_date) return `${label}: installment ${itemIndex + 1} due date is required.`;
      if (Number(item.grace_period_days || 0) < 0) {
        return `${label}: grace period cannot be negative.`;
      }
      if (item.late_fee_type !== 'NONE' && Number(item.late_fee_amount || 0) <= 0) {
        return `${label}: enter the late-fee amount for installment ${itemIndex + 1}.`;
      }
      total += Number(item.amount || 0);
    }
    if (Math.abs(total - Number(component.amount || 0)) > 0.001) {
      return `${label}: installment total (${money(total)}) must equal component amount (${money(component.amount)}).`;
    }
  }
  return '';
};

const ComponentEditor = ({ component, index, categories, onChange, onRemove, removable = true, academicYear = null }) => {
  const [expanded, setExpanded] = useState(true);

  const update = (key, value) => onChange(index, { ...component, [key]: value });

  const updateInstallment = (installmentIndex, key, value) => {
    const installments = [...(component.installments || [])];
    installments[installmentIndex] = {
      ...installments[installmentIndex],
      [key]: value,
    };
    update('installments', installments);
  };

  const addInstallment = () => {
    const installments = [...(component.installments || [])];
    installments.push(emptyInstallment(installments.length));
    update('installments', installments);
  };

  const removeInstallment = (installmentIndex) => {
    const installments = (component.installments || [])
      .filter((_, itemIndex) => itemIndex !== installmentIndex)
      .map((item, itemIndex) => ({ ...item, installment_no: itemIndex + 1 }));
    update('installments', installments);
  };

  const splitTwoInstallments = () => {
    const total = Number(component.amount || 0);
    const first = Math.floor((total / 2) * 100) / 100;
    const second = Number((total - first).toFixed(2));

    const startDate = String(academicYear?.start_date || '');
    const endDate = String(academicYear?.end_date || '');
    const calendarYear = Number(startDate.slice(0, 4));
    const withinAcademicYear = (value) =>
      Boolean(value && startDate && endDate && value >= startDate && value <= endDate);
    const may31 = calendarYear ? `${calendarYear}-05-31` : '';
    const nov30 = calendarYear ? `${calendarYear}-11-30` : '';

    update('installments', [
      {
        ...emptyInstallment(0),
        name: 'Term 1',
        amount: total > 0 ? first.toFixed(2) : '',
        due_date: withinAcademicYear(may31) ? may31 : '',
        grace_period_days: 7,
        late_fee_type: 'FIXED_PER_WEEK',
        late_fee_amount: '50.00',
      },
      {
        ...emptyInstallment(1),
        name: 'Term 2',
        amount: total > 0 ? second.toFixed(2) : '',
        due_date: withinAcademicYear(nov30) ? nov30 : '',
        grace_period_days: 7,
        late_fee_type: 'FIXED_PER_WEEK',
        late_fee_amount: '50.00',
      },
    ]);
  };

  return (
    <div className="rounded-xl border border-gray-200 bg-white">
      <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
        <button
          type="button"
          onClick={() => setExpanded((current) => !current)}
          className="flex min-w-0 flex-1 items-center gap-2 text-left"
        >
          {expanded ? <ChevronUp className="h-4 w-4 text-gray-400" /> : <ChevronDown className="h-4 w-4 text-gray-400" />}
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold text-gray-900">
              {component.name || `Fee Component ${index + 1}`}
            </p>
            <p className="text-xs text-gray-500">
              {humanize(component.frequency)} • {component.amount ? money(component.amount) : 'Amount not set'}
            </p>
          </div>
        </button>
        {removable && (
          <button
            type="button"
            onClick={() => onRemove(index)}
            className="rounded-lg p-2 text-red-500 hover:bg-red-50"
            title="Remove component"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        )}
      </div>

      {expanded && (
        <div className="space-y-5 p-4">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
            <Field label="Category" required>
              <select value={component.category} onChange={(event) => update('category', event.target.value)} className={inputClass}>
                <option value="">Select category</option>
                {categories.map((item) => (
                  <option key={item.id} value={item.id}>{item.name}</option>
                ))}
              </select>
            </Field>
            <Field label="Component Name" required>
              <input value={component.name} onChange={(event) => update('name', event.target.value)} className={inputClass} placeholder="Tuition Fee" />
            </Field>
            <Field label="Component Code" required>
              <input value={component.code} onChange={(event) => update('code', event.target.value.toUpperCase())} className={inputClass} placeholder="TUITION" />
            </Field>
            <Field label="Frequency" required>
              <select value={component.frequency} onChange={(event) => update('frequency', event.target.value)} className={inputClass}>
                {FREQUENCIES.map((value) => <option key={value} value={value}>{humanize(value)}</option>)}
              </select>
            </Field>
            <Field label="Annual / Total Amount" required>
              <input type="number" min="0.01" step="0.01" value={component.amount} onChange={(event) => update('amount', event.target.value)} className={inputClass} />
            </Field>
            <Field label="Student Scope" required>
              <select value={component.student_scope} onChange={(event) => update('student_scope', event.target.value)} className={inputClass}>
                {STUDENT_SCOPES.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
              </select>
            </Field>
            <Field label="Direct Due Date" hint="Used when this component has no installment schedule.">
              <input type="date" disabled={(component.installments || []).length > 0} value={component.due_date || ''} onChange={(event) => update('due_date', event.target.value)} className={inputClass} />
            </Field>
            <Field label="Status">
              <select value={component.status} onChange={(event) => update('status', event.target.value)} className={inputClass}>
                <option value="ACTIVE">Active</option>
                <option value="INACTIVE">Inactive</option>
              </select>
            </Field>
          </div>

          <div className="flex flex-wrap items-center gap-5 rounded-lg bg-gray-50 p-3">
            <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
              <input
                type="checkbox"
                checked={Boolean(component.is_mandatory)}
                onChange={(event) => update('is_mandatory', event.target.checked)}
                className="h-4 w-4 rounded border-gray-300 text-blue-600"
              />
              Mandatory fee
            </label>
            {!component.is_mandatory && (
              <span className="text-xs text-gray-500">
                Optional fees are assigned to individual students from Fee Collection.
              </span>
            )}
          </div>

          <div className="rounded-xl border border-blue-100 bg-blue-50/40 p-4">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h4 className="text-sm font-semibold text-gray-900">Installment Schedule</h4>
                <p className="mt-1 text-xs text-gray-500">
                  Installment amounts must exactly total the component amount.
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                <button type="button" onClick={splitTwoInstallments} className="rounded-lg border border-blue-200 bg-white px-3 py-2 text-xs font-semibold text-blue-700 hover:bg-blue-50">
                  2 Equal Terms + 7 Day Grace + ₹50/Week
                </button>
                <button type="button" onClick={addInstallment} className="inline-flex items-center gap-1 rounded-lg bg-blue-600 px-3 py-2 text-xs font-semibold text-white hover:bg-blue-700">
                  <Plus className="h-3.5 w-3.5" /> Add Installment
                </button>
              </div>
            </div>

            {(component.installments || []).length === 0 ? (
              <p className="mt-4 rounded-lg border border-dashed border-blue-200 bg-white p-4 text-center text-xs text-gray-500">
                No installments. The component will create a single fee liability using the direct due date.
              </p>
            ) : (
              <div className="mt-4 space-y-3">
                {component.installments.map((item, installmentIndex) => (
                  <div key={`${index}-${installmentIndex}`} className="rounded-lg border border-gray-200 bg-white p-3">
                    <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-7">
                      <Field label="#">
                        <input type="number" min="1" value={item.installment_no} onChange={(event) => updateInstallment(installmentIndex, 'installment_no', event.target.value)} className={inputClass} />
                      </Field>
                      <div className="xl:col-span-2">
                        <Field label="Name" required>
                          <input value={item.name} onChange={(event) => updateInstallment(installmentIndex, 'name', event.target.value)} className={inputClass} />
                        </Field>
                      </div>
                      <Field label="Amount" required>
                        <input type="number" min="0.01" step="0.01" value={item.amount} onChange={(event) => updateInstallment(installmentIndex, 'amount', event.target.value)} className={inputClass} />
                      </Field>
                      <Field label="Due Date" required>
                        <input type="date" value={item.due_date} onChange={(event) => updateInstallment(installmentIndex, 'due_date', event.target.value)} className={inputClass} />
                      </Field>
                      <Field label="Grace Days">
                        <input type="number" min="0" max="365" value={item.grace_period_days} onChange={(event) => updateInstallment(installmentIndex, 'grace_period_days', event.target.value)} className={inputClass} />
                      </Field>
                      <div className="flex items-end justify-end">
                        <button type="button" onClick={() => removeInstallment(installmentIndex)} className="mb-0.5 rounded-lg p-2.5 text-red-500 hover:bg-red-50" title="Remove installment">
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                    <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2">
                      <Field label="Late Fee Rule">
                        <select value={item.late_fee_type} onChange={(event) => updateInstallment(installmentIndex, 'late_fee_type', event.target.value)} className={inputClass}>
                          {LATE_FEE_TYPES.map((lateType) => <option key={lateType.value} value={lateType.value}>{lateType.label}</option>)}
                        </select>
                      </Field>
                      <Field label="Late Fee Amount">
                        <input type="number" min="0" step="0.01" disabled={item.late_fee_type === 'NONE'} value={item.late_fee_amount} onChange={(event) => updateInstallment(installmentIndex, 'late_fee_amount', event.target.value)} className={inputClass} />
                      </Field>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const FeeStructurePage = () => {
  const {
    schoolId,
    activeAcademicYear,
    activeAcademicYearId,
    academicYearLoading,
  } = useSchool();

  const [categories, setCategories] = useState([]);
  const [classes, setClasses] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [baseLoading, setBaseLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  const [groupModal, setGroupModal] = useState(false);
  const [groupForm, setGroupForm] = useState(emptyGroup);
  const [groupSaving, setGroupSaving] = useState(false);
  const [formError, setFormError] = useState('');

  const [categoryModal, setCategoryModal] = useState(false);
  const [categoryForm, setCategoryForm] = useState({ name: '', code: '', description: '', status: 'ACTIVE' });
  const [categorySaving, setCategorySaving] = useState(false);

  const [editingGroup, setEditingGroup] = useState(null);
  const [groupEditForm, setGroupEditForm] = useState({ name: '', code: '', description: '', status: 'ACTIVE' });
  const [editingComponent, setEditingComponent] = useState(null);
  const [componentEdit, setComponentEdit] = useState(emptyComponent);
  const [componentSaving, setComponentSaving] = useState(false);
  const [increaseReason, setIncreaseReason] = useState('');

  const loadBaseData = useCallback(async () => {
    if (!schoolId || !activeAcademicYearId) {
      setCategories([]);
      setClasses([]);
      return;
    }
    setBaseLoading(true);
    try {
      const [categoryResponse, classRows] = await Promise.all([
        feesApi.getFeeCategories(schoolId, { page_size: 200 }),
        academicApi.getClassLevels(schoolId, {
          academicYearId: activeAcademicYearId,
          status: 'ACTIVE',
        }),
      ]);
      setCategories((categoryResponse.items || []).filter((item) => item.status === 'ACTIVE'));
      setClasses(classRows || []);
    } catch (requestError) {
      setError(getFeesApiErrorMessage(requestError, 'Unable to load fee setup data.'));
    } finally {
      setBaseLoading(false);
    }
  }, [schoolId, activeAcademicYearId]);

  const loadGroups = useCallback(async () => {
    if (!schoolId || !activeAcademicYearId) {
      setGroups([]);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const response = await feesApi.getFeeConfigurationGroups(schoolId, {
        academicYearId: activeAcademicYearId,
        q: query.trim(),
        status: statusFilter,
        page_size: 100,
      });
      setGroups(response.items || []);
    } catch (requestError) {
      setError(getFeesApiErrorMessage(requestError, 'Unable to load fee configuration groups.'));
    } finally {
      setLoading(false);
    }
  }, [schoolId, activeAcademicYearId, query, statusFilter]);

  useEffect(() => {
    loadBaseData();
  }, [loadBaseData]);

  useEffect(() => {
    const timer = window.setTimeout(loadGroups, 250);
    return () => window.clearTimeout(timer);
  }, [loadGroups]);

  const openCreateGroup = () => {
    setGroupForm(emptyGroup());
    setFormError('');
    setGroupModal(true);
  };

  const updateComponent = (index, nextComponent) => {
    setGroupForm((current) => ({
      ...current,
      components: current.components.map((item, itemIndex) =>
        itemIndex === index ? nextComponent : item,
      ),
    }));
  };

  const removeComponent = (index) => {
    setGroupForm((current) => ({
      ...current,
      components: current.components.filter((_, itemIndex) => itemIndex !== index),
    }));
  };

  const toggleClass = (classId) => {
    setGroupForm((current) => {
      const exists = current.class_level_ids.includes(classId);
      return {
        ...current,
        class_level_ids: exists
          ? current.class_level_ids.filter((id) => id !== classId)
          : [...current.class_level_ids, classId],
      };
    });
  };

  const validateGroup = () => {
    if (!activeAcademicYearId) return 'Select a working academic year from the sidebar.';
    if (!groupForm.name.trim()) return 'Fee group name is required.';
    if (!groupForm.code.trim()) return 'Fee group code is required.';
    if (groupForm.class_level_ids.length === 0) return 'Select at least one class.';
    if (groupForm.components.length === 0) return 'Add at least one fee component.';
    for (let index = 0; index < groupForm.components.length; index += 1) {
      const message = validateComponent(groupForm.components[index], index);
      if (message) return message;
    }
    return '';
  };

  const createGroup = async (event) => {
    event.preventDefault();
    setFormError('');
    const validation = validateGroup();
    if (validation) {
      setFormError(validation);
      return;
    }

    const payload = {
      academic_year: activeAcademicYearId,
      name: groupForm.name.trim(),
      code: groupForm.code.trim().toUpperCase().replaceAll(' ', '_'),
      description: groupForm.description.trim(),
      status: groupForm.status,
      class_level_ids: groupForm.class_level_ids,
      components: groupForm.components.map(sanitizeComponent),
    };

    setGroupSaving(true);
    try {
      const response = await feesApi.createFeeConfigurationGroup(schoolId, payload);
      const sync = response?.data?.sync || {};
      setSuccess(
        `Fee group created. ${Number(sync.created_ledgers || 0)} student ledgers created, ` +
          `${Number(sync.optional_unassigned || 0)} optional fee assignments left unposted.`,
      );
      setGroupModal(false);
      await loadGroups();
    } catch (requestError) {
      setFormError(getFeesApiErrorMessage(requestError, 'Unable to create the fee group.'));
    } finally {
      setGroupSaving(false);
    }
  };

  const submitCategory = async (event) => {
    event.preventDefault();
    setFormError('');
    if (!categoryForm.name.trim() || !categoryForm.code.trim()) {
      setFormError('Category name and code are required.');
      return;
    }
    setCategorySaving(true);
    try {
      await feesApi.createFeeCategory(schoolId, {
        ...categoryForm,
        name: categoryForm.name.trim(),
        code: categoryForm.code.trim().toUpperCase().replaceAll(' ', '_'),
      });
      setCategoryModal(false);
      setCategoryForm({ name: '', code: '', description: '', status: 'ACTIVE' });
      setSuccess('Fee category created successfully.');
      await loadBaseData();
    } catch (requestError) {
      setFormError(getFeesApiErrorMessage(requestError, 'Unable to create the fee category.'));
    } finally {
      setCategorySaving(false);
    }
  };

  const openGroupEdit = (group) => {
    setEditingGroup(group);
    setGroupEditForm({
      name: group.name || '',
      code: group.code || '',
      description: group.description || '',
      status: group.status || 'ACTIVE',
    });
    setFormError('');
  };

  const saveGroupEdit = async (event) => {
    event.preventDefault();
    setGroupSaving(true);
    setFormError('');
    try {
      await feesApi.updateFeeConfigurationGroup(schoolId, editingGroup.id, {
        ...groupEditForm,
        name: groupEditForm.name.trim(),
        code: groupEditForm.code.trim().toUpperCase().replaceAll(' ', '_'),
      });
      setEditingGroup(null);
      setSuccess('Fee group updated successfully.');
      await loadGroups();
    } catch (requestError) {
      setFormError(getFeesApiErrorMessage(requestError, 'Unable to update the fee group.'));
    } finally {
      setGroupSaving(false);
    }
  };

  const openComponentEdit = (group, component) => {
    setEditingComponent({ group, component });
    setComponentEdit({
      category: component.category || '',
      code: component.code || '',
      name: component.name || '',
      frequency: component.frequency || 'ONE_TIME',
      amount: component.amount || '',
      due_date: component.due_date || '',
      late_fee_per_day: component.late_fee_per_day || '0.00',
      is_mandatory: Boolean(component.is_mandatory),
      student_scope: component.student_scope || 'ALL_STUDENTS',
      status: component.status || 'ACTIVE',
      installments: (component.installments || []).map((item) => ({ ...item })),
    });
    setIncreaseReason('');
    setFormError('');
  };

  const saveComponent = async (event) => {
    event.preventDefault();
    setFormError('');
    const validation = validateComponent(componentEdit, 0);
    if (validation) {
      setFormError(validation);
      return;
    }

    const oldAmount = Number(editingComponent?.component?.amount || 0);
    const newAmount = Number(componentEdit.amount || 0);
    if (newAmount < oldAmount) {
      setFormError('Fee amount cannot be decreased after the group has been created.');
      return;
    }
    if (newAmount > oldAmount && increaseReason.trim().length < 5) {
      setFormError('Enter a fee increase reason of at least 5 characters.');
      return;
    }

    setComponentSaving(true);
    try {
      await feesApi.updateFeeComponent(
        schoolId,
        editingComponent.group.id,
        editingComponent.component.id,
        {
          ...sanitizeComponent(componentEdit),
          increase_reason: increaseReason.trim(),
        },
      );
      setEditingComponent(null);
      setSuccess('Fee component updated across all classes in this group.');
      await loadGroups();
    } catch (requestError) {
      setFormError(getFeesApiErrorMessage(requestError, 'Unable to update the fee component.'));
    } finally {
      setComponentSaving(false);
    }
  };

  const syncGroup = async (group) => {
    const reason = window.prompt(
      'Optional: enter a reason if this sync contains fee increases. Leave blank for a normal reconciliation.',
      '',
    );
    if (reason === null) return;
    setError('');
    try {
      const response = await feesApi.syncFeeConfigurationGroup(schoolId, group.id, reason.trim());
      const data = response?.data || response || {};
      setSuccess(
        `Group synchronized. ${Number(data.created_ledgers || 0)} ledgers created, ` +
          `${Number(data.updated_unpaid_ledgers || 0)} unpaid ledgers updated, ` +
          `${Number(data.adjusted_paid_or_partial_ledgers || 0)} audited adjustments created.`,
      );
      await loadGroups();
    } catch (requestError) {
      setError(getFeesApiErrorMessage(requestError, 'Unable to synchronize the fee group.'));
    }
  };

  const deleteGroup = async (group) => {
    if (!window.confirm(`Delete fee group “${group.name}”? Groups with ledger history cannot be deleted.`)) return;
    setError('');
    try {
      await feesApi.deleteFeeConfigurationGroup(schoolId, group.id);
      setSuccess('Fee group deleted successfully.');
      await loadGroups();
    } catch (requestError) {
      setError(getFeesApiErrorMessage(requestError, 'Unable to delete the fee group.'));
    }
  };

  const totalConfigured = useMemo(
    () =>
      groups.reduce(
        (total, group) =>
          total +
          (group.components || []).reduce(
            (subtotal, component) => subtotal + Number(component.amount || 0),
            0,
          ),
        0,
      ),
    [groups],
  );

  if (academicYearLoading && !activeAcademicYearId) {
    return (
      <div className="flex min-h-[420px] items-center justify-center text-sm text-gray-500">
        <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Loading working academic year...
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-7xl space-y-6 pb-12">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-gray-900">Fee Structure</h1>
          <p className="mt-1 text-sm text-gray-500">
            Configure class groups, recurring fees, admission fees, optional items, installments and late-fee rules.
          </p>
          <p className="mt-2 text-xs font-semibold text-blue-700">
            Working year: {activeAcademicYear?.year_name || 'No academic year selected'}
          </p>
        </div>
        <div className="flex flex-wrap gap-3">
          <button
            type="button"
            disabled={!schoolId}
            onClick={() => {
              setFormError('');
              setCategoryModal(true);
            }}
            className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            <Plus className="h-4 w-4" /> Add Category
          </button>
          <button
            type="button"
            disabled={!activeAcademicYearId || baseLoading}
            onClick={openCreateGroup}
            className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
          >
            <Plus className="h-4 w-4" /> Create Fee Group
          </button>
        </div>
      </div>

      {error && <Notice>{error}</Notice>}
      {success && <Notice type="success">{success}</Notice>}

      {!activeAcademicYearId ? (
        <div className="rounded-2xl border border-amber-200 bg-amber-50 p-6 text-sm text-amber-800">
          Select an academic year from the global sidebar before configuring fees.
        </div>
      ) : (
        <>
          <section className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
              <div>
                <h2 className="font-semibold text-gray-900">Fee Groups</h2>
                <p className="mt-1 text-xs text-gray-500">
                  One group can target several classes while preserving class-wise ledgers and receipts.
                </p>
              </div>
              <div className="grid w-full gap-3 sm:grid-cols-2 lg:w-auto lg:min-w-[560px] lg:grid-cols-[1fr_160px_auto]">
                <input
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  placeholder="Search group, component, class..."
                  className="rounded-lg border border-gray-300 px-3 py-2.5 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                />
                <select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)} className="rounded-lg border border-gray-300 px-3 py-2.5 text-sm">
                  <option value="">All statuses</option>
                  <option value="ACTIVE">Active</option>
                  <option value="INACTIVE">Inactive</option>
                </select>
                <button type="button" onClick={loadGroups} className="inline-flex items-center justify-center rounded-lg border border-gray-300 p-2.5 text-gray-600 hover:bg-gray-50">
                  <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                </button>
              </div>
            </div>
          </section>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Groups</p>
              <p className="mt-2 text-2xl font-bold text-gray-900">{groups.length}</p>
            </div>
            <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Classes Available</p>
              <p className="mt-2 text-2xl font-bold text-gray-900">{classes.length}</p>
            </div>
            <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Configured Component Total</p>
              <p className="mt-2 text-2xl font-bold text-gray-900">{money(totalConfigured)}</p>
              <p className="mt-1 text-[11px] text-gray-400">Reference total only; not a school-wide payable total.</p>
            </div>
          </div>

          {loading ? (
            <div className="flex min-h-[300px] items-center justify-center rounded-2xl border border-gray-200 bg-white text-sm text-gray-500">
              <Loader2 className="mr-2 h-5 w-5 animate-spin text-blue-600" /> Loading fee groups...
            </div>
          ) : groups.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-gray-300 bg-white p-12 text-center">
              <p className="font-semibold text-gray-700">No fee groups found for {activeAcademicYear?.year_name}.</p>
              <p className="mt-1 text-sm text-gray-500">Create a grouped structure for one or more classes.</p>
            </div>
          ) : (
            <div className="space-y-5">
              {groups.map((group) => (
                <section key={group.id} className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
                  <div className="flex flex-col gap-4 border-b border-gray-200 bg-gray-50 px-5 py-4 lg:flex-row lg:items-start lg:justify-between">
                    <div>
                      <div className="flex flex-wrap items-center gap-2">
                        <h2 className="text-lg font-bold text-gray-900">{group.name}</h2>
                        <span className="rounded-md bg-blue-100 px-2 py-1 text-[11px] font-semibold text-blue-700">{group.code}</span>
                        <span className={`rounded-full px-2.5 py-1 text-[11px] font-semibold ${group.status === 'ACTIVE' ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-600'}`}>
                          {group.status}
                        </span>
                      </div>
                      {group.description && <p className="mt-1 text-sm text-gray-500">{group.description}</p>}
                      <div className="mt-3 flex flex-wrap gap-2">
                        {(group.classes || []).map((item) => (
                          <span key={item.id} className="rounded-full border border-gray-200 bg-white px-3 py-1 text-xs font-medium text-gray-700">
                            {item.class_name}
                          </span>
                        ))}
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <button type="button" onClick={() => syncGroup(group)} className="inline-flex items-center gap-1 rounded-lg border border-gray-300 bg-white px-3 py-2 text-xs font-semibold text-gray-700 hover:bg-gray-50">
                        <RefreshCw className="h-3.5 w-3.5" /> Sync Ledgers
                      </button>
                      <button type="button" onClick={() => openGroupEdit(group)} className="inline-flex items-center gap-1 rounded-lg border border-gray-300 bg-white px-3 py-2 text-xs font-semibold text-gray-700 hover:bg-gray-50">
                        <Edit2 className="h-3.5 w-3.5" /> Edit Group
                      </button>
                      <button type="button" onClick={() => deleteGroup(group)} className="inline-flex items-center gap-1 rounded-lg border border-red-200 bg-white px-3 py-2 text-xs font-semibold text-red-600 hover:bg-red-50">
                        <Trash2 className="h-3.5 w-3.5" /> Delete
                      </button>
                    </div>
                  </div>

                  <div className="overflow-x-auto">
                    <table className="w-full min-w-[1050px] text-sm">
                      <thead className="bg-white text-xs uppercase tracking-wide text-gray-500">
                        <tr>
                          <th className="px-5 py-3 text-left">Component</th>
                          <th className="px-5 py-3 text-left">Category</th>
                          <th className="px-5 py-3 text-left">Frequency</th>
                          <th className="px-5 py-3 text-left">Scope</th>
                          <th className="px-5 py-3 text-left">Type</th>
                          <th className="px-5 py-3 text-right">Amount</th>
                          <th className="px-5 py-3 text-left">Schedule</th>
                          <th className="px-5 py-3 text-center">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(group.components || []).map((component) => (
                          <tr key={component.id} className="border-t border-gray-100 align-top">
                            <td className="px-5 py-4">
                              <p className="font-semibold text-gray-900">{component.name}</p>
                              <p className="mt-1 text-xs text-gray-400">{component.code}</p>
                            </td>
                            <td className="px-5 py-4 text-gray-700">{component.category_name}</td>
                            <td className="px-5 py-4 text-gray-700">{humanize(component.frequency)}</td>
                            <td className="px-5 py-4 text-gray-700">{humanize(component.student_scope)}</td>
                            <td className="px-5 py-4">
                              <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${component.is_mandatory ? 'bg-green-50 text-green-700' : 'bg-purple-50 text-purple-700'}`}>
                                {component.is_mandatory ? 'Mandatory' : 'Optional'}
                              </span>
                            </td>
                            <td className="px-5 py-4 text-right font-bold text-gray-900">{money(component.amount)}</td>
                            <td className="px-5 py-4">
                              {(component.installments || []).length > 0 ? (
                                <div className="space-y-1.5">
                                  {component.installments.map((installment) => (
                                    <div key={installment.id || installment.installment_no} className="text-xs text-gray-600">
                                      <span className="font-semibold text-gray-800">{installment.name}</span>
                                      {' · '}{money(installment.amount)} · {formatDate(installment.due_date)}
                                      {Number(installment.grace_period_days || 0) > 0 ? ` · ${installment.grace_period_days}d grace` : ''}
                                      {installment.late_fee_type !== 'NONE' ? ` · ${money(installment.late_fee_amount)} ${humanize(installment.late_fee_type)}` : ''}
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <span className="text-xs text-gray-500">Single due {formatDate(component.due_date)}</span>
                              )}
                            </td>
                            <td className="px-5 py-4 text-center">
                              <button type="button" onClick={() => openComponentEdit(group, component)} className="rounded-lg p-2 text-orange-500 hover:bg-orange-50" title="Edit component across all classes">
                                <Edit2 className="h-4 w-4" />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </section>
              ))}
            </div>
          )}
        </>
      )}

      {categoryModal && (
        <Modal title="Add Fee Category" maxWidth="max-w-xl" onClose={() => setCategoryModal(false)}>
          <div className="space-y-4">
            <Notice>{formError}</Notice>
            <form onSubmit={submitCategory} className="space-y-4">
              <Field label="Category Name" required>
                <input value={categoryForm.name} onChange={(event) => setCategoryForm((current) => ({ ...current, name: event.target.value }))} className={inputClass} />
              </Field>
              <Field label="Category Code" required>
                <input value={categoryForm.code} onChange={(event) => setCategoryForm((current) => ({ ...current, code: event.target.value.toUpperCase() }))} className={inputClass} />
              </Field>
              <Field label="Description">
                <textarea rows={3} value={categoryForm.description} onChange={(event) => setCategoryForm((current) => ({ ...current, description: event.target.value }))} className={inputClass} />
              </Field>
              <div className="flex justify-end gap-3">
                <button type="button" onClick={() => setCategoryModal(false)} className="rounded-lg border border-gray-300 px-4 py-2.5 text-sm font-semibold">Cancel</button>
                <button disabled={categorySaving} className="rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white disabled:opacity-50">
                  {categorySaving ? 'Saving...' : 'Create Category'}
                </button>
              </div>
            </form>
          </div>
        </Modal>
      )}

      {groupModal && (
        <Modal
          title="Create Fee Configuration Group"
          subtitle={`${activeAcademicYear?.year_name || ''} · applies to every selected class`}
          onClose={() => !groupSaving && setGroupModal(false)}
        >
          <form onSubmit={createGroup} className="space-y-6">
            <Notice>{formError}</Notice>

            <section className="rounded-xl border border-gray-200 p-4">
              <h3 className="font-semibold text-gray-900">1. Group Details</h3>
              <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
                <Field label="Group Name" required>
                  <input value={groupForm.name} onChange={(event) => setGroupForm((current) => ({ ...current, name: event.target.value }))} className={inputClass} placeholder="Primary 1st to 5th" />
                </Field>
                <Field label="Group Code" required>
                  <input value={groupForm.code} onChange={(event) => setGroupForm((current) => ({ ...current, code: event.target.value.toUpperCase() }))} className={inputClass} placeholder="PRIMARY_1_5" />
                </Field>
                <div className="md:col-span-2">
                  <Field label="Description">
                    <textarea rows={2} value={groupForm.description} onChange={(event) => setGroupForm((current) => ({ ...current, description: event.target.value }))} className={inputClass} />
                  </Field>
                </div>
              </div>
            </section>

            <section className="rounded-xl border border-gray-200 p-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-semibold text-gray-900">2. Classes</h3>
                  <p className="mt-1 text-xs text-gray-500">Choose every class that uses this same fee configuration.</p>
                </div>
                <button
                  type="button"
                  onClick={() => setGroupForm((current) => ({ ...current, class_level_ids: classes.map((item) => item.id) }))}
                  className="text-xs font-semibold text-blue-600 hover:text-blue-700"
                >
                  Select all
                </button>
              </div>
              <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6">
                {classes.map((item) => {
                  const checked = groupForm.class_level_ids.includes(item.id);
                  return (
                    <label key={item.id} className={`flex cursor-pointer items-center gap-2 rounded-lg border p-3 text-sm font-medium ${checked ? 'border-blue-300 bg-blue-50 text-blue-700' : 'border-gray-200 text-gray-700 hover:bg-gray-50'}`}>
                      <input type="checkbox" checked={checked} onChange={() => toggleClass(item.id)} className="h-4 w-4 rounded border-gray-300 text-blue-600" />
                      <span className="truncate">{item.class_name || item.name}</span>
                    </label>
                  );
                })}
              </div>
            </section>

            <section className="rounded-xl border border-gray-200 p-4">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <h3 className="font-semibold text-gray-900">3. Fee Components & Installments</h3>
                  <p className="mt-1 text-xs text-gray-500">Add admission, tuition, development, lab, board, uniform/books or other fee components.</p>
                </div>
                <button
                  type="button"
                  onClick={() => setGroupForm((current) => ({ ...current, components: [...current.components, emptyComponent()] }))}
                  className="inline-flex items-center gap-1 rounded-lg bg-gray-900 px-3 py-2 text-xs font-semibold text-white hover:bg-gray-800"
                >
                  <Plus className="h-3.5 w-3.5" /> Add Component
                </button>
              </div>
              <div className="mt-4 space-y-4">
                {groupForm.components.map((component, index) => (
                  <ComponentEditor
                    key={index}
                    index={index}
                    component={component}
                    categories={categories}
                    onChange={updateComponent}
                    onRemove={removeComponent}
                    removable={groupForm.components.length > 1}
                  />
                ))}
              </div>
            </section>

            <div className="sticky bottom-0 -mx-6 -mb-6 flex items-center justify-between border-t border-gray-200 bg-white px-6 py-4">
              <p className="text-xs text-gray-500">
                {groupForm.class_level_ids.length} class(es) · {groupForm.components.length} component(s)
              </p>
              <div className="flex gap-3">
                <button type="button" disabled={groupSaving} onClick={() => setGroupModal(false)} className="rounded-lg border border-gray-300 px-4 py-2.5 text-sm font-semibold text-gray-700 disabled:opacity-50">Cancel</button>
                <button disabled={groupSaving} className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50">
                  {groupSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                  {groupSaving ? 'Creating...' : 'Create Fee Group'}
                </button>
              </div>
            </div>
          </form>
        </Modal>
      )}

      {editingGroup && (
        <Modal title="Edit Fee Group" subtitle="Group metadata only; class membership is fixed after creation." maxWidth="max-w-xl" onClose={() => setEditingGroup(null)}>
          <form onSubmit={saveGroupEdit} className="space-y-4">
            <Notice>{formError}</Notice>
            <Field label="Name" required><input value={groupEditForm.name} onChange={(event) => setGroupEditForm((current) => ({ ...current, name: event.target.value }))} className={inputClass} /></Field>
            <Field label="Code" required><input value={groupEditForm.code} onChange={(event) => setGroupEditForm((current) => ({ ...current, code: event.target.value.toUpperCase() }))} className={inputClass} /></Field>
            <Field label="Description"><textarea rows={3} value={groupEditForm.description} onChange={(event) => setGroupEditForm((current) => ({ ...current, description: event.target.value }))} className={inputClass} /></Field>
            <Field label="Status"><select value={groupEditForm.status} onChange={(event) => setGroupEditForm((current) => ({ ...current, status: event.target.value }))} className={inputClass}><option value="ACTIVE">Active</option><option value="INACTIVE">Inactive</option></select></Field>
            <div className="flex justify-end gap-3"><button type="button" onClick={() => setEditingGroup(null)} className="rounded-lg border border-gray-300 px-4 py-2.5 text-sm font-semibold">Cancel</button><button disabled={groupSaving} className="rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white disabled:opacity-50">{groupSaving ? 'Saving...' : 'Save Group'}</button></div>
          </form>
        </Modal>
      )}

      {editingComponent && (
        <Modal title="Edit Fee Component" subtitle={`${editingComponent.group.name} · changes propagate to every class in this group`} onClose={() => setEditingComponent(null)}>
          <form onSubmit={saveComponent} className="space-y-5">
            <Notice>{formError}</Notice>
            <ComponentEditor index={0} component={componentEdit} categories={categories} onChange={(_, next) => setComponentEdit(next)} onRemove={() => {}} removable={false} academicYear={activeAcademicYear} />
            {Number(componentEdit.amount || 0) > Number(editingComponent.component.amount || 0) && (
              <Field label="Reason for Fee Increase" required hint="Stored in the fee audit trail. Existing receipts are not rewritten.">
                <textarea rows={3} value={increaseReason} onChange={(event) => setIncreaseReason(event.target.value)} className={inputClass} placeholder="Explain why this fee is being increased..." />
              </Field>
            )}
            <div className="flex justify-end gap-3"><button type="button" onClick={() => setEditingComponent(null)} className="rounded-lg border border-gray-300 px-4 py-2.5 text-sm font-semibold">Cancel</button><button disabled={componentSaving} className="rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white disabled:opacity-50">{componentSaving ? 'Saving...' : 'Save Component'}</button></div>
          </form>
        </Modal>
      )}
    </div>
  );
};

export default FeeStructurePage;
